<script>
import { mixin as clickaway } from 'vue-clickaway'
import cancelIcon from '@/assets/images/icons/cancel-alt.svg?inline'
import { nToWord } from '@/utils/helpers'

export default {
  name: 'RexTourists',
  components: {
    cancelIcon
  },
  mixins: [
    clickaway
  ],
  props: {
    name: {
      type: String,
      default: () => null,
      required: true
    },
    storeModule: {
      type: String,
      default: () => null,
      required: true
    },
    target: {
      type: String,
      default: () => null,
      required: true
    },
    getter: {
      type: String,
      default: () => 'getField'
    },
    setter: {
      type: String,
      default: () => 'setField'
    },
    label: {
      type: String,
      default: () => null
    },
    position: {
      type: String,
      default: () => null
    },
    maxAdultsCount: {
      type: Number,
      default: () => 4
    },
    defaultAdultsCount: {
      type: Number,
      default: () => 2
    },
    maxChildrenCount: {
      type: Number,
      default: () => 6
    },
    minChildAge: {
      type: Number,
      default: () => 2
    },
    maxChildAge: {
      type: Number,
      default: () => 12
    },
    maxRoomsCount: {
      type: Number,
      default: 5
    },
    hasBackendValidation: {
      type: Boolean,
      default: () => null
    },
    isMultiple: {
      type: Boolean,
      default: () => false
    },
    isDisabled: {
      type: Boolean,
      default: () => false
    },
    showOnlyCount: {
      type: Boolean,
      default: () => false
    },
    useShorten: {
      type: Boolean,
      default: () => false
    }
  },
  data: () => ({
    isActive: false
  }),
  computed: {
    value() {
      return this.$store.getters[`${this.storeModule}/${this.getter}`](this.target)
    },
    error() {
      let error = null

      if (this.hasBackendValidation) {
        const target = this.target.split('.')
        const err = this.$store.getters[`${this.storeModule}/${this.getter}`](`${target[0]}Errors`)
          .find(item => item.source === this.name)

        error = err ? err.detail : null
      }

      return error
    },
    childAges() {
      return [
        `< ${this.minChildAge}`,
        ...Array(this.maxChildAge - 1)
          .fill(this.minChildAge)
          .map((item, index) => (`${item + index}`))
      ]
    },
    separateCount() {
      const adultsCount = this.value.reduce((sum, room) => (sum + room.adults), 0)
      const childrenCount = this.value.reduce((sum, room) => (sum + room.children), 0)

      let tourists = this.getCountText(adultsCount, Array(3).fill('взр.'), ['взрослый', 'взрослых', 'взрослых'])

      const children = this.getCountText(childrenCount, ['реб.', 'реб.', 'дет.'], ['ребенок', 'ребенка', 'детей'])

      if (childrenCount) {
        tourists = `${tourists}, ${children}`
      }

      return tourists
    },
    totalCount() {
      const count = this.value.reduce((sum, room) => (sum + room.adults + room.children), 0)
      const label = this.getCountText(count, Array(3).fill('тур.'), ['турист', 'туриста', 'туристов'])

      return `${count} ${label}`
    },
  },
  watch: {
    value: {
      handler(val) {
        this.updateValue(val)
      },
      deep: true
    }
  },
  methods: {
    getCountText(count, short, full) {
      return this.useShorten
        ? `${count} ${nToWord(count, short)}`
        : `${count} ${nToWord(count, full)}`
    },
    hideTouristsForm() {
      this.$refs.dropdown.isActive = false
    },
    ageLabel: age => nToWord(age, ['год', 'года', 'лет']),
    adultsLabel: count => nToWord(count, ['взрослый', 'взрослых', 'взрослых']),
    updateValue(value) {
      this.$store.commit(`${this.storeModule}/${this.setter}`, {
        target: this.target,
        value,
      })

      if (this.error !== null && this.hasBackendValidation) {
        const target = this.target.split('.')
        const err = this.$store.getters[`${this.storeModule}/${this.getter}`](`${target[0]}Errors`)
        const currentFieldErrorIndex = err.map(x => x.source).indexOf(this.name)

        err.splice(currentFieldErrorIndex, 1)

        this.$store.commit(`${this.storeModule}/${this.setter}`, err)
      }

      this.$emit('change', {
        event: value,
        element: this.$vnode.elm
      })
    },
    addRoom() {
      this.value.push({
        adults: this.defaultAdultsCount,
        ages: [],
        children: 0
      })
    },
    removeRoom(roomIndex) {
      this.value.splice(roomIndex, 1)
    },
    addChild(room, age) {
      room.children += 1
      room.ages.push(age)

      this.ageSelector = null
    },
    removeChild(room, childIndex) {
      room.children -= 1
      room.ages.splice(childIndex, 1)
    }
  }
}
</script>
<template>
  <b-field
    custom-class="rex-label"
    :label="label"
    :type="{'is-danger': error}"
    :message="error"
  >
    <b-dropdown
      ref="dropdown"
      v-on-clickaway="hideTouristsForm"
      class="rex-tourists"
      :position="position"
      :disabled="isDisabled"
      :can-close="false"
      @active-change="isActive = $event"
    >
      <input
        slot="trigger"
        :value="showOnlyCount ? totalCount : separateCount"
        readonly
        class="rex-tourists__trigger"
        :disabled="isDisabled"
        :class="{
          'rex-tourists__trigger--active': isActive,
          'rex-tourists__trigger--has-error': errors.first(name) || error
        }"
      >
      <b-dropdown-item
        class="rex-tourists__form"
        custom
      >
        <div
          v-for="(room, roomIndex) in value"
          :key="roomIndex"
          class="rex-tourists__form-item"
        >
          <div
            v-if="isMultiple"
            class="rex-tourists__form-item-header"
          >
            <span>Номер {{ roomIndex + 1 }}</span>
            <button
              v-if="value.length > 1"
              class="rex-tourists__form-delete-button"
              @click="removeRoom(roomIndex)"
            >
              <cancel-icon
                fill="#ababab"
              />
            </button>
          </div>
          <div class="rex-tourists__form-tourist">
            <b-button
              type="is-primary"
              class="rex-button2"
              :disabled="room.adults === 1"
              @click="room.adults -= 1"
            >
              -
            </b-button>
            <span>{{ `${room.adults} ${adultsLabel(room.adults)}` }}</span>
            <b-button
              type="is-primary"
              class="rex-button2"
              :disabled="room.adults === maxAdultsCount"
              @click="room.adults += 1"
            >
              +
            </b-button>
          </div>
          <div
            v-for="child in room.children"
            :key="`${roomIndex}-child-${child}`"
            class="rex-tourists__form-child"
          >
            <div class="rex-tourists__form-child-trigger">
              <span>
                Ребенок, {{ room.ages[child - 1] }} {{ ageLabel(room.ages[child - 1]) }}
              </span>
              <button
                class="rex-tourists__form-delete-button"
                @click="removeChild(room, child - 1)"
              >
                <cancel-icon
                  fill="#ababab"
                />
              </button>
            </div>
          </div>
          <b-dropdown
            v-if="room.children < maxChildrenCount"
            class="rex-tourists__form-add-child"
          >
            <b-button
              slot="trigger"
              type="is-light"
              outlined
              class="rex-button2"
            >
              Добавить ребенка
            </b-button>
            <b-dropdown-item
              v-for="age in childAges"
              :key="`${roomIndex}-child-age-${age}`"
              class="rex-select__option"
              @click="addChild(room, age)"
            >
              {{ age }} {{ ageLabel(age) }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div
          v-if="isMultiple && value.length < maxRoomsCount"
          class="rex-tourists__form-item"
        >
          <b-button
            type="is-light"
            outlined
            class="rex-button2"
            @click="addRoom"
          >
            Добавить комнату
          </b-button>
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </b-field>
</template>